export type ListaFornecedorPdf = {
  fornecedor: string;
  previsaoChegada?: string | null;
  dataTransferencia?: string | null;
  itens: { quantidade?: string | null; unidade?: string | null; descricao: string }[];
};

export type PdfFichaTransferenciaInput = {
  origem: string;
  destino: string;
  dataReferencia: string;
  previsaoEntrega: string;
  itens: { descricao: string; quantidadeTransferencia: string; unidade?: string }[];
  listas?: ListaFornecedorPdf[];
};

export function formatarDataReferenciaBR(iso: string): string {
  const [ano, mes, dia] = iso.split("-");
  if (!ano || !mes || !dia) return iso;
  return `${dia}/${mes}/${ano}`;
}

const DIAS_SEMANA = [
  "Domingo",
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado",
];

export function diaSemanaDaData(iso: string): string {
  const [ano, mes, dia] = iso.split("-").map(Number);
  if (!ano || !mes || !dia) return "";
  return DIAS_SEMANA[new Date(Date.UTC(ano, mes - 1, dia)).getUTCDay()] ?? "";
}

/** "16/10/2026 (Sexta-feira)" ou, sem data própria, a previsão de entrega da ficha. */
export function rotuloTransferenciaLista(
  dataTransferencia: string | null | undefined,
  previsaoEntregaFicha: string
): string {
  const iso = (dataTransferencia ?? "").trim();
  if (!iso) {
    return previsaoEntregaFicha ? `com a ficha (${previsaoEntregaFicha})` : "com a ficha";
  }
  const dia = diaSemanaDaData(iso);
  return dia ? `${formatarDataReferenciaBR(iso)} (${dia})` : formatarDataReferenciaBR(iso);
}

export function detalheListaFornecedor(
  lista: ListaFornecedorPdf,
  previsaoEntregaFicha: string
): string {
  const chegada = (lista.previsaoChegada ?? "").trim();
  return [
    chegada ? `Chegada: ${chegada}` : "",
    `Transferência: ${rotuloTransferenciaLista(lista.dataTransferencia, previsaoEntregaFicha)}`,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function nomeArquivoFichaTransferencia(dataISO: string): string {
  const [ano, mes, dia] = dataISO.split("-");
  const data = ano && mes && dia ? `${dia}-${mes}-${ano}` : dataISO;
  return `Ficha Transferencia ${data}.pdf`;
}

export function linhasPdfFichaTransferencia(
  itens: PdfFichaTransferenciaInput["itens"]
): string[][] {
  return itens.map((item) => {
    const quantidade = item.quantidadeTransferencia.trim() || "0";
    const unidade = (item.unidade ?? "").trim();
    return [item.descricao.trim() || "—", unidade ? `${quantidade} ${unidade}` : quantidade];
  });
}

export function quantidadeListaFornecedor(
  item: ListaFornecedorPdf["itens"][number]
): string {
  return [item.quantidade, item.unidade]
    .map((parte) => (parte ?? "").trim())
    .filter(Boolean)
    .join(" ");
}

export function linhasPdfListaFornecedor(
  itens: ListaFornecedorPdf["itens"]
): string[][] {
  return itens
    .filter((item) => item.descricao.trim())
    .map((item) => [item.descricao.trim(), quantidadeListaFornecedor(item) || "—"]);
}

export function listasPreenchidas(
  listas: ListaFornecedorPdf[] | undefined
): ListaFornecedorPdf[] {
  return (listas ?? []).filter(
    (lista) =>
      lista.fornecedor.trim() || lista.itens.some((item) => item.descricao.trim())
  );
}

function linhaTexto(quantidade: string, descricao: string): string {
  return quantidade ? `• ${quantidade} — ${descricao}` : `• ${descricao}`;
}

/** Texto único (ficha diária + fornecedores), com negrito no formato do WhatsApp. */
export function textoConsolidadoFichaTransferencia(
  input: PdfFichaTransferenciaInput
): string {
  const blocos: string[] = [
    [
      "*FICHA DE TRANSFERÊNCIA*",
      `${input.origem} para ${input.destino}`,
      `Ficha diária: ${formatarDataReferenciaBR(input.dataReferencia)}`,
      `Previsão de entrega: ${input.previsaoEntrega || "—"}`,
    ].join("\n"),
  ];

  const producao = linhasPdfFichaTransferencia(input.itens).map(
    ([descricao, quantidade]) => linhaTexto(quantidade, descricao)
  );
  blocos.push(
    ["*FICHA DIÁRIA DE PRODUÇÃO*", ...(producao.length ? producao : ["• Nenhum item"])].join("\n")
  );

  for (const lista of listasPreenchidas(input.listas)) {
    const titulo = `*${(lista.fornecedor.trim() || "Fornecedor").toUpperCase()}*`;
    const linhas = linhasPdfListaFornecedor(lista.itens).map(([descricao, quantidade]) =>
      linhaTexto(quantidade === "—" ? "" : quantidade, descricao)
    );
    blocos.push(
      [
        titulo,
        detalheListaFornecedor(lista, input.previsaoEntrega),
        ...(linhas.length ? linhas : ["• Nenhum item"]),
      ].join("\n")
    );
  }

  return blocos.join("\n\n");
}
